import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { errorMsg } from '@/lib/recuentos';
import { ajustesAPI, EstadoBadge } from './ajustesApi';

const NombreEditable = ({ value, onSave, testId }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  if (!edit) return (
    <span className="flex items-center gap-2 font-medium text-slate-800">{value}
      <button type="button" onClick={() => setEdit(true)} className="text-slate-400 hover:text-orange-600" data-testid={`${testId}-edit`}><Pencil className="w-3.5 h-3.5" /></button>
    </span>
  );
  const save = async () => { if (await onSave(v)) setEdit(false); };
  return (
    <span className="flex items-center gap-1">
      <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} className="h-8 w-56" autoFocus data-testid={`${testId}-input`} />
      <Button size="sm" variant="ghost" onClick={save} data-testid={`${testId}-save`}><Check className="w-4 h-4 text-green-600" /></Button>
      <Button size="sm" variant="ghost" onClick={() => { setV(value); setEdit(false); }}><X className="w-4 h-4" /></Button>
    </span>
  );
};

export { NombreEditable };

export const BodegasTab = () => {
  const [rows, setRows] = useState([]);
  const [nueva, setNueva] = useState('');
  const load = () => ajustesAPI.bodegas().then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const update = async (b, data, okMsg) => {
    try { await ajustesAPI.updateBodega(b.id, data); toast.success(okMsg); load(); return true; }
    catch (e) { toast.error(errorMsg(e, 'Error al guardar')); return false; }
  };
  const toggle = (b, active) => {
    if (!active && (b.locales.some((l) => l.is_active) || b.productos_con_stock)) {
      toast.error(`${b.name} abastece ${b.locales.filter((l) => l.is_active).length} locales activos y tiene stock en ${b.productos_con_stock} productos. Reasigna los locales y deja su stock en 0 antes de desactivarla.`);
      return;
    }
    if (!active && !window.confirm(`¿Desactivar ${b.name}? No se borra: su historial queda intacto.`)) return;
    update(b, { is_active: active }, active ? 'Bodega activada' : 'Bodega desactivada');
  };
  const crear = async () => {
    try { await ajustesAPI.createBodega(nueva); toast.success('Bodega creada'); setNueva(''); load(); }
    catch (e) { toast.error(errorMsg(e, 'Error al crear')); }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex gap-2">
        <Input placeholder="Nombre de la nueva bodega" value={nueva} onChange={(e) => setNueva(e.target.value)} className="w-72" data-testid="bodega-nueva-input" />
        <Button onClick={crear} disabled={!nueva.trim()} className="bg-orange-500 hover:bg-orange-600" data-testid="bodega-crear-btn"><Plus className="w-4 h-4 mr-1" /> Crear bodega</Button>
      </div>
      <p className="text-xs text-slate-500">Cambiar el nombre no afecta stock ni historial: todo se relaciona por ID. Las bodegas no se borran, solo se desactivan.</p>
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm" data-testid="bodegas-table">
          <thead className="bg-slate-50 text-slate-600"><tr>
            <th className="px-4 py-2 text-left">Nombre</th><th className="px-4 py-2 text-left">Locales que abastece</th>
            <th className="px-4 py-2 text-center">Productos con stock</th><th className="px-4 py-2 text-center">Estado</th><th className="px-4 py-2 text-center">Activa</th>
          </tr></thead>
          <tbody>{rows.map((b) => (
            <tr key={b.id} className={`border-t ${b.is_active ? '' : 'opacity-60'}`} data-testid={`bodega-row-${b.id}`}>
              <td className="px-4 py-2"><NombreEditable value={b.name} testId={`bodega-nombre-${b.id}`} onSave={(v) => update(b, { name: v }, 'Nombre actualizado')} /></td>
              <td className="px-4 py-2 text-xs text-slate-600">{b.locales.length ? b.locales.map((l) => <span key={l.id} className={`inline-block mr-1 mb-0.5 px-2 py-0.5 rounded-full ${l.is_active ? 'bg-orange-50 text-orange-700' : 'bg-slate-100 text-slate-400 line-through'}`}>{l.name}</span>) : '—'}</td>
              <td className="px-4 py-2 text-center" data-testid={`bodega-stock-${b.id}`}>{b.productos_con_stock}</td>
              <td className="px-4 py-2 text-center"><EstadoBadge active={b.is_active} testId={`bodega-estado-${b.id}`} /></td>
              <td className="px-4 py-2 text-center"><Switch checked={b.is_active} onCheckedChange={(c) => toggle(b, c)} data-testid={`bodega-activa-${b.id}`} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};
